import { useMemo, useState } from 'react';

import type { InstantSurvey, InstantSurveyType } from '@api/instant_survey/types';
import { SURVEY_TYPES } from '@api/instant_survey';
import type { SurveyResponseDetails } from '@api/survey_dashboard/types';
import {
  Card,
  DEFAULT_CHART_COLORS,
  isCalypsoSurvey,
  Layout,
  Lightbox,
  Pill,
  ProgressBar,
} from '@utilities';

import GroupAdvancedPrompts from '../../../../../CreateInstantSurvey/components/Panelists/components/QuotaGroupCard/components/GroupAdvancedPrompts';
import GroupMadlib from '../../../../../CreateInstantSurvey/components/Panelists/components/QuotaGroupCard/components/GroupMadlib';
import {
  getIsPreviewDisabled,
  PreviewModal,
  useSurveyPreview,
} from '../../../../../../components/SurveyPreview';
import Questionnaire from '../Questionnaire';
import ReportQuestionnaire from './components/Questionnaire';
import type { MergedExternalQuestion } from '../../../../utilities/types';

import styles from './_index.module.scss';

interface DetailsProps {
  canShowFullQuestionnaire?: boolean;
  details?: SurveyResponseDetails;
  survey: InstantSurvey;
  surveyQuestions: MergedExternalQuestion[];
  surveyType: InstantSurveyType;
}

const Details = ({
  canShowFullQuestionnaire = false,
  details,
  survey,
  surveyQuestions,
  surveyType,
}: DetailsProps) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const isMonadicSurvey = surveyType.id === SURVEY_TYPES.MONADIC;
  const { conceptImages, groupImageIndex } = useMemo(() => {
    const images: { alt: string; caption: string; src: string }[] = [];
    const indexByGroupId: Record<number, number> = {};

    survey.groups.forEach((group) => {
      if (group?.conceptImage?.cdnUrl) {
        indexByGroupId[group.id] = images.length;
        images.push({
          alt: `Concept for ${group.name}`,
          caption: group?.conceptName || group.name,
          src: group.conceptImage.cdnUrl,
        });
      }
    });

    return { conceptImages: images, groupImageIndex: indexByGroupId };
  }, [survey.groups]);

  const surveyPreviewQuestions = survey.questions;
  const canPreviewSurvey = isCalypsoSurvey(survey);
  const {
    handleClosePreviewModal,
    handlePreviewButtonClick,
    handleUpdatePreviewLanguage,
    handleUpdatePreviewQuestion,
    handleUpdatePreviewQuotaGroup,
    languages,
    previewQuestions,
    previewSelection,
    resetPreviewQuestion,
    showPreviewModal,
  } = useSurveyPreview({
    languageInfo: survey.languageInfo,
    questionLogic: survey.questionLogic,
    questions: surveyPreviewQuestions,
    quotaGroups: survey.groups,
  });
  const isPreviewButtonDisabled = getIsPreviewDisabled(previewQuestions);
  const shouldShowReportQuestionnaire = canShowFullQuestionnaire && canPreviewSurvey;

  const handleOpenLightbox = (groupId: number) => {
    const imageIndex = groupImageIndex[groupId];
    if (imageIndex === undefined) return;
    setLightboxIndex(imageIndex);
    setIsLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
  };

  return (
    <div className={styles['details']}>
      <h3>Quota {survey.groups.length > 1 ? 'Groups' : 'Group'}</h3>
      <Layout.Flex.Column className={styles['details-list']}>
        {survey.groups.map((group, index) => (
          <Card className={styles['details-card']} key={group.id} status="info">
            <h2>
              <span
                className={styles['details-group-color']}
                style={{ background: DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length] }}
              />
              {group.name}
            </h2>
            {group?.continuouslyFed && <Pill isCompleted value="Continuously Fed Sample" />}
            <Layout.Flex gap="x-large">
              <Layout.Fill>
                {/* @ts-ignore GroupMadlib is not typed yet */}
                <GroupMadlib group={group} isReadOnly surveyType={surveyType} />
                {surveyType.advancedPrompts?.length > 0 && (
                  <GroupAdvancedPrompts
                    // @ts-ignore GroupAdvancedPrompts is not typed yet
                    advancedPrompts={surveyType.advancedPrompts}
                    group={group}
                    isCompleted
                  />
                )}
              </Layout.Fill>
              <div className={styles['details-group-divider']} />
              <Layout.Sidebar>
                <div
                  className={styles['details-group-response-counts']}
                  id={`response-counts-${group.id}`}
                >
                  Requested Quota <strong>{group.quotaLimit}</strong>
                </div>
                <ProgressBar
                  aria-labelledby={`response-counts-${group.id}`}
                  max={group.quotaLimit}
                  value={
                    details?.completesByQuotaGroup?.[group.id] || (group.responseCount ?? undefined)
                  }
                />
                {isMonadicSurvey && (group?.conceptImage?.cdnUrl || group?.conceptName) && (
                  <div className={styles['details-concept']}>
                    {group?.conceptImage?.cdnUrl && (
                      <button
                        aria-label={`Open concept for ${group.name}`}
                        className={styles['details-concept-button']}
                        onClick={() => handleOpenLightbox(group.id)}
                        type="button"
                      >
                        <img alt={`Concept for ${group.name}`} src={group?.conceptImage?.cdnUrl} />
                      </button>
                    )}
                    {group?.conceptName && <p>{group?.conceptName}</p>}
                  </div>
                )}
              </Layout.Sidebar>
            </Layout.Flex>
          </Card>
        ))}
      </Layout.Flex.Column>

      {shouldShowReportQuestionnaire ? (
        <ReportQuestionnaire
          canPreviewSurvey={canPreviewSurvey}
          isPreviewButtonDisabled={isPreviewButtonDisabled}
          onPreviewButtonClick={handlePreviewButtonClick}
          survey={survey}
          surveyType={surveyType}
        />
      ) : (
        <Questionnaire
          canPreviewSurvey={canPreviewSurvey}
          isPreviewButtonDisabled={isPreviewButtonDisabled}
          onPreviewButtonClick={handlePreviewButtonClick}
          surveyQuestions={surveyQuestions}
        />
      )}

      {isMonadicSurvey && conceptImages.length > 0 && (
        <Lightbox
          currentImageIndex={lightboxIndex}
          images={conceptImages}
          isActive={isLightboxOpen}
          onClose={handleCloseLightbox}
        />
      )}
      {canPreviewSurvey && showPreviewModal && (
        <PreviewModal
          country={survey.country}
          instantSurveyId={survey.id!}
          isActive={showPreviewModal}
          languages={languages}
          onClose={handleClosePreviewModal}
          onLanguageChange={handleUpdatePreviewLanguage}
          onQuestionChange={handleUpdatePreviewQuestion}
          onQuotaGroupChange={handleUpdatePreviewQuotaGroup}
          onRestart={resetPreviewQuestion}
          previewSelection={previewSelection}
          questions={previewQuestions}
          quotaGroups={survey.groups}
        />
      )}
    </div>
  );
};

export default Details;
